import { useState, useEffect } from "react";
import { Music, Heart, Users, Award } from "lucide-react";
import axios from "axios";

import { API, resolveImg } from "@/config";

const DEFAULT_ABOUT_IMG = "https://img.youtube.com/vi/l_wIOO4ZR_w/maxresdefault.jpg";
const DEFAULT_GALLERY_IMG = "https://img.youtube.com/vi/JAl_labp1Ck/maxresdefault.jpg";

export default function AboutPage() {
  const [aboutImg, setAboutImg] = useState(DEFAULT_ABOUT_IMG);
  const [aboutText, setAboutText] = useState({
    history_p1: "Founded in 1996, the Good Samaritans' Choir Bwaise has grown from a small group of devoted singers into one of the most vibrant and beloved choirs in the Bwaise community of Kampala, Uganda.",
    history_p2: "For nearly three decades, our choir has been dedicated to spreading the Gospel through the gift of music. Our motto, \"Love And Unity In God,\" guides everything we do -- from our Sunday worship performances to our community outreach programs.",
    history_p3: "What started as a handful of passionate voices has blossomed into a thriving musical ministry that touches the hearts of many. We are united by our love for God, our commitment to each other, and our desire to serve our community.",
    mission: "Our mission is to glorify God through music, to build a community of love and unity, and to reach out to those in need with the compassion of the Good Samaritan. Through our songs, our service, and our fellowship, we strive to be a light in Bwaise and beyond."
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API}/settings`);
        if (data.about_image) setAboutImg(resolveImg(data.about_image));
        if (data.about_text) setAboutText(prev => ({ ...prev, ...data.about_text }));
      } catch (err) {
        console.error("Failed to load about settings:", err.message);
      }
    };
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch on mount; API is a module constant

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section
        className="relative py-24 md:py-32"
        style={{ backgroundImage: `url(${aboutImg})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 section-container text-center">
          <p className="text-[#FAD6A5] text-sm uppercase tracking-widest font-semibold mb-3">Our Story</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white text-shadow tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            About Us
          </h1>
        </div>
      </section>

      {/* History */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#C4392D] text-sm uppercase tracking-widest font-semibold mb-3">Since 1996</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Our History
              </h2>
              <div className="space-y-4 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
                <p>{aboutText.history_p1}</p>
                <p>{aboutText.history_p2}</p>
                <p>{aboutText.history_p3}</p>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src={DEFAULT_GALLERY_IMG} alt="Choir performance" className="w-full h-[400px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white">
        <div className="section-container">
          <div className="text-center mb-12">
            <p className="text-[#C4392D] text-sm uppercase tracking-widest font-semibold mb-3">What We Stand For</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Our Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, title: "Love", desc: "We love God and love one another as ourselves. Love is at the core of everything we do." },
              { icon: Users, title: "Unity", desc: "We stand together as one body, united by faith and a shared mission to spread God's word." },
              { icon: Music, title: "Worship", desc: "Music is our offering to God. We pour our hearts into every song, every note, every performance." },
              { icon: Award, title: "Service", desc: "As Good Samaritans, we are called to serve. We give back to our community through outreach." },
            ].map((value) => (
              <div key={value.title} className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow rounded-2xl p-6 text-center" data-testid={`value-card-${value.title.toLowerCase()}`}>
                <div className="w-14 h-14 bg-[#FFF3E6] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <value.icon size={24} className="text-[#E8922F]" />
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{value.title}</h3>
                <p className="text-[#4A4A4A] text-sm leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding bg-[#FFF3E6]">
        <div className="section-container text-center max-w-3xl mx-auto">
          <p className="text-[#C4392D] text-sm uppercase tracking-widest font-semibold mb-3">Our Mission</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Love And Unity In God
          </h2>
          <p className="text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            {aboutText.mission}
          </p>
        </div>
      </section>
    </div>
  );
}
